/**
 * Managed-business invite landing page.
 *
 * Service-bureau-created businesses receive an email link of the shape
 * ``/:businessSlug/managed-business/invite?token=<jwt>``. Visiting the
 * link does NOT authenticate the user on its own — the token is only a
 * credential to call the password-setup endpoint. After the owner picks
 * a password and submits, the backend sets it, issues auth cookies, and
 * we refresh the auth store and redirect into the onboarding wizard
 * already signed in.
 *
 * This page never trusts ``businessSlug`` from the URL for anything
 * sensitive; the backend resolves the bureau and managed business from
 * the token. The slug is just used for branding lookups and as the
 * landing point for the post-auth redirect.
 */
import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
  type MantineColor,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconLock,
  IconShieldCheck,
} from "@tabler/icons-react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import PasswordRequirements, {
  isLocallyValidPassword,
} from "@/components/PasswordRequirements";
import { AppRoutes } from "@/constants";
import { OnboardingStep } from "@pages/Onboarding/components/OnboardingStep";
import { OnboardingSkeleton } from "@pages/Onboarding/components/OnboardingSkeleton";
import { WizzyAvatar } from "@pages/Onboarding/components/WizzyAvatar";
import { StepContextProvider } from "@pages/Onboarding/context/StepContext";
import "@pages/Onboarding/Onboarding.scss";
import "@pages/Onboarding/components/OnboardingStep.scss";
import { WIZZY_IMAGES_BY_STEP } from "@pages/Onboarding/wizzyConfig";
import { useBranding } from "@/pages/LoginManagement/hooks/useBranding";
import { useAuthStore } from "@/utils/stores/authStore";
import type { ManagedBusinessInviteStatus } from "@smartwiz/shared-types";
import {
  completeManagedBusinessInvite,
  fetchManagedBusinessInviteStatus,
} from "./api";

interface SubmitState {
  loading: boolean;
  message: string | null;
  color: "red" | "blue" | "teal" | null;
}

const INVITE_WIZZY = WIZZY_IMAGES_BY_STEP.accountCreation;

const stepContextValue = {
  isActive: true,
  isPast: false,
  stepIndex: 0,
  isLastStep: true,
  totalSteps: 1,
  fullScreen: false,
} as const;

/**
 * Build the white-label login path when the invite URL includes a bureau slug.
 */
const loginPathForSlug = (businessSlug: string | undefined): string => {
  if (businessSlug) {
    return `/${businessSlug}${AppRoutes.businessLogin}`;
  }
  return AppRoutes.login;
};

interface InviteTokenContentProps {
  status: ManagedBusinessInviteStatus;
  primaryTint: MantineColor;
  loginHref: string;
  password: string;
  setPassword: Dispatch<SetStateAction<string>>;
  reenteredPassword: string;
  setReenteredPassword: Dispatch<SetStateAction<string>>;
  confirmError: string;
  submitState: SubmitState;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

/**
 * Single-card invite copy plus password form or sign-in CTA when the invite was
 * already completed.
 */
function ManagedBusinessInviteTokenContent({
  status,
  primaryTint,
  loginHref,
  password,
  setPassword,
  reenteredPassword,
  setReenteredPassword,
  confirmError,
  submitState,
  handleSubmit,
}: InviteTokenContentProps) {
  const first = (status.ownerFirstName || "").trim();
  const headline = first
    ? `Welcome to SmartWiz, ${first}.`
    : "Welcome to SmartWiz.";
  const bureauName = status.serviceBureau.name || "Your service bureau";
  const businessName = status.businessName || "your new SmartWiz business";

  if (!status.mustSetPassword) {
    return (
      <Paper radius="md" shadow="xs" p={{ base: "lg", sm: "xl" }}>
        <Stack gap="lg">
          <Stack gap="xs">
            <Title
              order={2}
              ta="center"
              fw={700}
              fz="clamp(1.35rem, 4vw, 1.65rem)"
            >
              {headline}
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Your account for <strong>{businessName}</strong> is ready. Sign in
              with the email and password you already use for SmartWiz.
            </Text>
          </Stack>
          <Button
            component={Link}
            to={loginHref}
            radius="md"
            size="md"
            color={primaryTint}
            fullWidth
          >
            Sign in
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper radius="lg" withBorder shadow="lg" p={{ base: "lg", sm: "xl" }}>
      <Stack gap="xl">
        <Stack gap="sm">
          <Title
            order={2}
            ta="center"
            fw={700}
            fz="clamp(1.35rem, 4vw, 1.75rem)"
          >
            {headline}
          </Title>
          <Text size="sm" c="dimmed" ta="center" lh={1.55}>
            <strong>{bureauName}</strong> invited you to{" "}
            <strong>{businessName}</strong>. Set a password you&apos;ll use to
            sign in, then we&apos;ll walk you through a short setup.
          </Text>
          {status.ownerEmail ? (
            <Text size="xs" c="dimmed" ta="center">
              {status.ownerEmail}
            </Text>
          ) : null}
        </Stack>

        <Divider />

        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Stack className="styled-inputs" gap="md">
              <PasswordRequirements
                value={password}
                setValue={setPassword}
                placeholder="Create a password"
                name="newPassword"
                variant="unstyled"
                radius="md"
                size="xs"
                withAsterisk={false}
              />
              <PasswordInput
                placeholder="Confirm password"
                name="confirmPassword"
                radius="md"
                size="xs"
                variant="unstyled"
                className="password"
                leftSection={<IconLock color="#A3A9B0" />}
                value={reenteredPassword}
                error={confirmError}
                onChange={(event) =>
                  setReenteredPassword(event.currentTarget.value)
                }
              />
            </Stack>

            <Button
              type="submit"
              color={primaryTint}
              radius="md"
              size="md"
              fullWidth
              loading={submitState.loading}
              disabled={submitState.loading}
            >
              Continue to onboarding
            </Button>

            {submitState.message && submitState.color && (
              <Alert
                color={submitState.color}
                icon={
                  submitState.color === "red" ? (
                    <IconAlertCircle size={18} />
                  ) : undefined
                }
              >
                {submitState.message}
              </Alert>
            )}

            <Group gap={6} justify="center" wrap="nowrap">
              <IconShieldCheck
                size={16}
                stroke={1.5}
                color="var(--mantine-color-gray-6)"
                style={{ flexShrink: 0 }}
              />
              <Text size="xs" c="dimmed" ta="center" lh={1.35}>
                Your information is encrypted and never sold.
              </Text>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
}

const ManagedBusinessInvitePage = () => {
  const { businessSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const refreshAuth = useAuthStore((state) => state.refreshAuth);

  const token = searchParams.get("token");

  const [tokenStatus, setTokenStatus] =
    useState<ManagedBusinessInviteStatus | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState<boolean>(true);

  const [password, setPassword] = useState<string>("");
  const [reenteredPassword, setReenteredPassword] = useState<string>("");
  const [confirmError, setConfirmError] = useState<string>("");
  const [submitState, setSubmitState] = useState<SubmitState>({
    loading: false,
    message: null,
    color: null,
  });

  const { businessInfo, loading: brandingLoading } = useBranding();

  const primaryTint = useMemo((): MantineColor => {
    if (businessSlug && businessInfo?.theme?.primaryColor) {
      return businessInfo.theme.primaryColor as MantineColor;
    }
    return "blue";
  }, [businessSlug, businessInfo?.theme?.primaryColor]);

  const loginHref = loginPathForSlug(businessSlug);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setTokenLoading(false);
      setTokenError(
        "This invite link is missing its token. Please ask your service bureau to resend the email."
      );
      return () => {
        cancelled = true;
      };
    }
    setTokenLoading(true);
    fetchManagedBusinessInviteStatus(token).then((status) => {
      if (cancelled) {
        return;
      }
      if (status === null) {
        setTokenError(
          "This invite link is invalid or has expired. Please ask your service bureau to resend the email."
        );
      } else {
        setTokenStatus(status);
        setTokenError(null);
      }
      setTokenLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setConfirmError("");
    setSubmitState({ loading: false, message: null, color: null });

    if (!token) {
      setSubmitState({
        loading: false,
        message:
          "This invite link is missing its token. Please ask your service bureau to resend the email.",
        color: "red",
      });
      return;
    }
    if (password !== reenteredPassword) {
      setConfirmError("Passwords do not match.");
      return;
    }
    if (!isLocallyValidPassword(password)) {
      setSubmitState({
        loading: false,
        message: "Password does not meet the requirements above.",
        color: "red",
      });
      return;
    }

    setSubmitState({
      loading: true,
      message: "Setting up your account...",
      color: "blue",
    });
    try {
      await completeManagedBusinessInvite(token, password);
    } catch (error: unknown) {
      const fallback =
        "We could not finish setting up your account. Please try again or ask your service bureau to resend the invite.";
      const apiMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || fallback;
      setSubmitState({ loading: false, message: apiMessage, color: "red" });
      return;
    }

    try {
      await refreshAuth();
    } catch (error) {
      console.error(
        "Managed-business invite: auth refresh failed after password set",
        error
      );
      setSubmitState({
        loading: false,
        message:
          "Password saved, but we could not log you in automatically. Please use the login page.",
        color: "red",
      });
      return;
    }

    setSubmitState({
      loading: false,
      message: "Account ready — redirecting to onboarding…",
      color: "teal",
    });
    navigate(AppRoutes.gettingStarted, { replace: true });
  };

  if (brandingLoading) {
    return <OnboardingSkeleton />;
  }

  return (
    <Box className="onboarding-container">
      <Box className="onboarding-content">
        <Box className="onboarding-wizzy-column">
          <Box className="wizzy-legend-wrapper">
            <Box
              className="wizzy-avatar-container"
              style={{ marginBottom: "2rem" }}
            >
              <WizzyAvatar
                wizzyImagePre={INVITE_WIZZY.default}
                wizzyImagePost={INVITE_WIZZY.hover ?? INVITE_WIZZY.default}
              />
            </Box>
          </Box>
        </Box>

        <Box className="onboarding-steps-column">
          <Box className="step-fade-container">
            <Box style={{ width: "100%", maxWidth: 440, margin: "0 auto" }}>
              <Stack gap="md" pb="lg" pt={{ base: "md", sm: 0 }}>
                {tokenLoading && (
                  <Box className="step-loader">
                    <Loader size="lg" />
                  </Box>
                )}

                {!tokenLoading && tokenError && (
                  <StepContextProvider value={stepContextValue}>
                    <OnboardingStep
                      stepId="accountCreation"
                      title="Invite link unavailable"
                      description="We could not validate this invitation."
                    >
                      <Stack gap="md" mt="md">
                        <Alert icon={<IconAlertCircle size={18} />} color="red">
                          {tokenError}
                        </Alert>
                        <Text size="sm" ta="center" c="dimmed">
                          Need a hand? Reply to the invite email or contact your
                          service bureau administrator.
                        </Text>
                      </Stack>
                    </OnboardingStep>
                  </StepContextProvider>
                )}

                {!tokenLoading && tokenStatus && (
                  <StepContextProvider value={stepContextValue}>
                    <ManagedBusinessInviteTokenContent
                      status={tokenStatus}
                      primaryTint={primaryTint}
                      loginHref={loginHref}
                      password={password}
                      setPassword={setPassword}
                      reenteredPassword={reenteredPassword}
                      setReenteredPassword={setReenteredPassword}
                      confirmError={confirmError}
                      submitState={submitState}
                      handleSubmit={handleSubmit}
                    />
                  </StepContextProvider>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ManagedBusinessInvitePage;
